import loglevel from 'loglevel'
import { defaultsDeep } from 'lodash'
import { v4 as uuidV4 } from 'uuid'
import { isAndroid, isIOS } from '../browser'

export const EVT_PAGE_VISITED = 10001
export const EVT_PAGE_LOADING = 10002
export const EVT_PAGE_STAYING = 10003
export const EVT_ITEM_EXPOSED = 10004
export const EVT_CLICK = 10100

const isEnvDevelopment = process.env.NODE_ENV !== 'production'

const SESSION_ID = uuidV4().substr(-4) + `_${Date.now()}`

const logger = loglevel.getLogger('JSBridge')

const LIST_PROD = ['pr', 'eu', 'us']

const EVT_FN_NOT_EXISTS = 'EVT_FN_NOT_EXISTS'
const EVT_TIMEOUT = 'EVT_TIMEOUT'

// 判断范围宽松些
const TIMEOUT = 300

function ready(fn) {
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    setTimeout(fn, 1)
  } else {
    document.addEventListener('DOMContentLoaded', fn, false)
  }
}

if (typeof window !== 'undefined') {
  ready(() => {
    /** 供 App 调用 */
    const eufyBridge = {
      /**
       * 切换暗黑模式通知
       * @param  {[type]} type [0: 正常模式  1： 暗黑模式]
       */
      toggleTheme(type) {
        document.body.classList.toggle('dark-theme', type + '' === '1')
        logger.info(`eufyBridge.toggleTheme: ${type}`)
        // TODO: 增加事件通知
      },
    }

    window.eufyBridge = eufyBridge
  })
}

// 非 production 环境并且非 pr 环境, 则按默认展示
if (
  process.env.NODE_ENV === 'production' &&
  LIST_PROD.includes(process.env.REACT_APP_ENV)
) {
  logger.setLevel('error')
} else {
  logger.enableAll()
}

// TODO: 判断回调是否存在, 待实现
function hasJSBridgeFunction(fnName) {
  if (isIOS) {
  }
  if (isAndroid) {
  }
  return false
}

function assembleAbsoluteUrl(url) {
  let absoluteUrl = url
  // 判断绝对路径还是相对路径
  if (/^https?:\/\//.test(url)) {
    // discard
  } else {
    const location = window.location
    const hostname = location.hostname
    const port = location.port ? `:${location.port}` : ''
    const protocol = location.protocol
    absoluteUrl = [
      `${protocol}//${hostname}${port}`,
      `${url}`.replace(/\/+/g, '/'),
    ].join('')
  }
  return absoluteUrl
}

function setupWebViewJavascriptBridge(callback) {
  if (typeof window === 'undefined') return ''
  // 目前 iOS App 使用到的方案
  if (window.top.WebViewJavascriptBridge) {
    return callback(window.top.WebViewJavascriptBridge)
  }
  // FIXME: 待确定哪里用到, 如果没用到则可以删除
  if (window.top.WVJBCallbacks) {
    return window.top.WVJBCallbacks.push(callback)
  }
  window.top.WVJBCallbacks = [callback]
  const docEl = document.documentElement
  const WVJBIframe = document.createElement('iframe')
  WVJBIframe.style.display = 'none'
  WVJBIframe.src = 'wvjbscheme://__bridge_loaded__'
  docEl.appendChild(WVJBIframe)
  setTimeout(() => {
    docEl.removeChild(WVJBIframe)
  })
}

/**
 *
 * @param {string} cmd 方法名
 * @param {...any} params iOS 只能传入一个参数, Android 传入参数根据实际接口要求
 * @returns
 */
function runCommand(cmd, ...params) {
  if (isAndroid) {
    const fn = window.EufyJS?.[cmd]
    if (typeof fn === 'function') {
      // 方法不能解构使用, 否则会报 injected 错误, 需要通过对象方法去调用
      return Promise.resolve(window.EufyJS[cmd](...params))
    }
    return Promise.reject({
      code: EVT_FN_NOT_EXISTS,
      message: `API: ${cmd} not found.`,
    })
  }
  if (isIOS) {
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, {
          code: EVT_FN_NOT_EXISTS,
          message: `API: ${cmd} not found.`,
        }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge(bridge => {
        bridge.callHandler(cmd, ...params, response => {
          resolve(response)
        })
      })
    })
  }
}

export function track(params) {
  logger.info(`API track called with: ${JSON.stringify(params)}`)
  if (isIOS) {
    return runCommand('logEventWithParam', { ...params, key_event: true })
  }
  if (isAndroid) {
    return runCommand('collectData', JSON.stringify(params))
  }
}

export function setNavbar(config) {
  logger.info(`API setNavbar called with: ${JSON.stringify(config)}`)
  if (isAndroid) {
    return runCommand('navBarStatusChange', JSON.stringify(config))
  }
  return runCommand('navBarStatusChange', config)
}

export function getNativeInfo() {
  if (isAndroid) {
    return Promise.resolve(runCommand('getNativeInfo')).then(resp =>
      JSON.parse(resp)
    )
  } else {
    // ios
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, { message: 'Get native info timeout.' }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge(bridge => {
        bridge.callHandler('getNativeInfo', {}, response => {
          resolve(response)
        })
      })
    })
  }
}

export function uploadHelpEvent(key, helpful, level) {
  logger.info(`API uplaodHelpEvent called.`)
  if (isIOS) {
    return runCommand('logFAQEvent', {
      key,
      level: String(level),
      helpful: String(helpful),
    })
  }
  if (isAndroid) {
    // Android 传入为多个参数
    return runCommand('reportFaqEvent', key, String(level), String(helpful))
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/**
 * 返回上一个 App 页面, ***不要在 Explore 首页调用***
 * @returns
 */
export function backToNative() {
  logger.info('API backTonative called.')
  return runCommand('backToNative')
}

// 打开邮箱
export function openEmail() {
  let iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = 'eufysecurity://eufy/explore/openEmailClient'
  document.body.appendChild(iframe)
}

let appName

/** 避免反复调用, 并且处理兼容问题 */
export async function getAppName() {
  if (typeof appName === 'undefined') {
    // 与运营平台上的数据对齐
    const REACT_APP_NAME = (process.env.REACT_APP_NAME || '')
      .replace(/^(eufy)(.+)$/, '$1 $2')
      .toLowerCase()
    try {
      const nativeInfo = await getNativeInfo()
      appName = nativeInfo.appName || REACT_APP_NAME
    } catch {
      appName = REACT_APP_NAME
    }
  }
  return appName || 'eufy security'
}

/**
 * 上报埋点数据
 * @param params
 * @returns
 */
export function uploadTrackings(params) {
  const { events } = params

  const date = new Date()
  const year = date.getFullYear()
  const months = `0${date.getMonth() + 1}`.substr(-2)
  const days = `0${date.getDate()}`.substr(-2)
  const hours = `0${date.getHours()}`.substr(-2)
  const minutes = `0${date.getMinutes()}`.substr(-2)
  const seconds = `0${date.getSeconds()}`.substr(-2)

  events.forEach(v => {
    defaultsDeep(v, {
      device_sn: '',
      device_model: '',
      station_sn: '',
      station_model: '',
      extra: '', // 其他参数
      session_id: SESSION_ID, // 供大数据区分
      local_time: `${year}-${months}-${days} ${hours}:${minutes}:${seconds}`, // 本地时间(yyyy-MM-dd HH:mm:ss)
      ts: Date.now(),
    })
  })
  if (isAndroid) {
    // 注意, android 接受 json 字符串参数
    return runCommand('uploadTrackings', JSON.stringify(params)).catch(
      console.err
    )
  }
  return runCommand('uploadTrackings', params).catch(console.error)
}

export function openWebview(url) {
  const iframe = document.createElement('iframe')
  let absoluteUrl = assembleAbsoluteUrl(url)
  // 地址补全, 以当前地址进行处理
  if (absoluteUrl.startsWith('/')) {
    absoluteUrl = `${getHTTPAddress()}/absoluteUrl`
  }
  iframe.style.display = 'none'
  iframe.src = `eufysecurity://eufy/explore/web#${absoluteUrl}`
  document.body.appendChild(iframe)
  logger.info(`API openWebview called with URL: ${absoluteUrl}`)
  console.log(`API openWebview called with URL: ${absoluteUrl}`)
  setTimeout(() => {
    document.body.removeChild(iframe)
  })
}

export function openBrowser(url) {
  let absoluteUrl = assembleAbsoluteUrl(url)
  if (absoluteUrl.indexOf('?') > -1) {
    absoluteUrl = absoluteUrl.replace(/&?$/, '&jump=2')
  } else {
    absoluteUrl = `${absoluteUrl}?jump=2`
  }
  logger.info(`API openBrowser called with URL: ${absoluteUrl}`)
  if (isIOS) {
    const iframeEle = document.createElement('iframe')
    iframeEle.style.display = 'none'
    iframeEle.src = absoluteUrl
    document.body.appendChild(iframeEle)
    setTimeout(() => {
      document.body.removeChild(iframeEle)
    })
  } else {
    window.location.href = absoluteUrl
  }
}

// 调用app原生支付方法
export function startPay(data) {
  return runCommand('startPay', JSON.stringify(data))
}

/** 加密 */
export async function encrypt(data) {
  logger.info(`API encrypt called.`, data)
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join('')
  if (appVersion >= '4.5.1') {
    if (isAndroid) {
      return runCommand('encrypt', JSON.stringify(data))
        .then(resp => JSON.parse(resp))
        .then(res => {
          if (res.code === 0) {
            return res.data
          } else {
            return Promise.reject(`Encrypt Error: ${res}`)
          }
        })
    } else if (isIOS) {
      return runCommand('encrypt', data).then(res => {
        if (res.code === 0) {
          return res.data
        } else {
          return Promise.reject(`Encrypt Error: ${res}`)
        }
      })
    }
  } else {
    if (isIOS || isAndroid) {
      return runCommand('aesEncrypt', String(data.data))
    }
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/** 解密 */
export async function decrypt(data) {
  logger.info(`API decrypt called.`)
  if (typeof data.data === 'object') {
    return data.data
  }
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join('')
  if (appVersion >= '4.5.1') {
    if (isAndroid) {
      return runCommand('decrypt', JSON.stringify(data))
        .then(resp => JSON.parse(resp))
        .then(res => {
          if (res.code === 0) {
            return JSON.parse(res.data)
          } else {
            return Promise.reject(`Decrypt Error: ${res}`)
          }
        })
    } else if (isIOS) {
      return runCommand('decrypt', data).then(res => {
        if (res.code === 0) {
          return JSON.parse(res.data)
        } else {
          return Promise.reject(`Decrypt Error: ${res}`)
        }
      })
    }
    return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
  } else {
    return data.data
  }
}

export function togglePullToRefresh(isRefreshable) {
  if (isAndroid) {
    runCommand('togglePullToRefresh', isRefreshable)
  }
}

// v3 app Header 解密
export function getHeaderV3(ts, once, body) {
  // console.log(`API getHeaderV3 called.`, { ts, once, body })
  if (isAndroid) {
    return runCommand('getHeaderV3', ts, once, body).then(res =>
      JSON.parse(res)
    )
  }
  if (isIOS) {
    return runCommand('getHeaderV3', JSON.stringify({ ts, once, body }))
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/** 加密 v3 app */
export async function encryptV3(data) {
  logger.info(`API encryptV3 called.`, data)
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join('')
  if (appVersion >= '4.5.1') {
    if (isAndroid) {
      return runCommand('encryptV3', JSON.stringify(data))
        .then(resp => JSON.parse(resp))
        .then(res => {
          if (res.code === 0) {
            return res.data
          } else {
            return Promise.reject(`EncryptV3 Error: ${JSON.parse(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand('encryptV3', data).then(res => {
        if (res.code === 0) {
          return res.data
        } else {
          return Promise.reject(`EncryptV3 Error: ${JSON.parse(res)}`)
        }
      })
    }
  } else {
    if (isIOS || isAndroid) {
      return runCommand('aesEncrypt', String(data.data))
    }
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/**
 * v3 app 解密
 * app方法 decryptV3
 * */
export async function decryptV3App(data) {
  if (!data.data || typeof data.data === 'object') {
    return data.data
  }
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join('')
  if (appVersion >= '4.5.1') {
    if (isAndroid) {
      return runCommand('decryptV3', JSON.stringify(data))
        .then(resp => JSON.parse(resp))
        .then(res => {
          if (res.code === 0) {
            return JSON.parse(res.data)
          } else {
            return Promise.reject(`decryptV3 Error: ${JSON.stringify(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand('decryptV3', data).then(res => {
        if (res.code === 0) {
          return JSON.parse(res.data)
        } else {
          return Promise.reject(`decryptV3 Error: ${JSON.stringify(res)}`)
        }
      })
    }
    return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
  } else {
    return data.data
  }
}

// v3 h5解密
export async function decryptV3H5(data, shareKey) {
  if (!data) {
    return data
  }
  const decryptjson = await AesCBCDecryptV3(data, shareKey)
  try {
    return JSON.parse(decryptjson)
  } catch (error) {
    return decryptjson
  }
}
